import constants from '@/constants';
import ProductItem from './productItem/ProductItem.vue';
import { createApp } from 'vue';
import { AmzNav } from './navigator';
import { AsinTag } from './tags';
import { AmzLoading } from './loading';
import { BackTop } from './backTop';
import { AmzPullRefresh } from './pullRefresh';
import { useIntersectionObserver } from '@vueuse/core';

const { NEW_IMAGE_URL } = constants;
const DEFAULT_URL = `${NEW_IMAGE_URL}10_Assets/01_DummyImage/DummyImage_Asin_list_01_3x.png`;

// Common Component Collection
const compDatas = [
  AmzNav,
  AsinTag,
  BackTop,
  AmzLoading,
  AmzPullRefresh,
  ProductItem
];
// Mainly used to add dom
const appendEl = el => {
  el.appendChild(el.instance.$el);
};
// Mainly used to remove dom
const removeEl = el => {
  el.removeChild(el.instance.$el);
};
// Register custom public components, directives, etc. in the project
export const registerGlobComp = app => {
  // Define the v-lazy instruction, mainly used for lazy image loading
  app.directive('lazy', {
    mounted(el, { value }) {
      // UseIntersectionObserver(el,callback:{isIntersecting}): Detects that a target element's visibility.
      // El: dom to monitor
      // IsIntersecting: Is it currently visible
      // If isIntersecting is true, within the visible range, stop listening, execute the stop() method, and assign a path to the current image
      // If loading fails while listening to images, change the path of the current image to the default image path that failed to load
      const { stop } = useIntersectionObserver(el, ([{ isIntersecting }]) => {
        if (isIntersecting) {
          stop();
          el.src = value;
          el.onerror = function () {
            el.parentNode.className += ' amzn-placeholder-image';
            if (el.src !== DEFAULT_URL) {
              el.src = DEFAULT_URL;
            }
          };
        }
      });
    }
  });
  // Reload images
  // Some icon images may fail to load due to network fluctuations. In this case,
  // we will ask the image to be reloaded. If it cannot be loaded after loading three times, it will end
  app.directive('reload-img', {
    mounted(el, { value }) {
      let limit = 3;
      el.src = value;
      el.onerror = function () {
        if (limit <= 0) {
          return;
        }
        limit -= 1;
        el.src = value;
      };
    }
  });
  // Define the v-loading instruction, mainly used to display loaded animations at the time of interface requests
  app.directive('loading', {
    // Implementation ideas:
    // Mounted: If the parameter passed is true, it is necessary to give the current parent relative positioning.
    // According to the method of adding dom, add a masked loading animation to the parent dom
    // Updated: After parameter modification, this lifecycle will be executed, and the explicit and implicit loading of animation can be defined based on state changes
    mounted(el, binding) {
      const appLoading = createApp(AmzLoading);
      const instance = appLoading.mount(document.createElement('div'));
      el.instance = instance;
      el.style.position = 'relative';
      if (binding.value) {
        appendEl(el);
      }
    },
    updated(el, binding) {
      if (binding.value !== binding.oldValue) {
        // eslint-disable-next-line no-unused-expressions
        binding.value ? appendEl(el) : removeEl(el);
      }
    }
  });
  // Use custom components using the public component array defined above the loop
  compDatas.forEach(item => {
    app.use(item);
  });
};
